import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { BaseHttpService } from '@framework/services';
import { environment } from '@app/env/environment';

export interface PhotoGalleryApiModel {
    id: number;
    name: string;
    is_active: boolean;
    /** Shown when no campaign galleries apply (no campaign, or none attached). */
    include_in_default?: boolean;
    media_count?: number;
    time_created?: string;
    time_updated?: string;
}

export interface PhotoGalleryMediaItemApiModel {
    id: number;
    sort_order: number;
    is_active: boolean;
    media: {
        id: number;
        is_active?: boolean;
        image?: {
            id?: number;
            url?: string;
            origname?: string;
            filename?: string;
            size?: number;
            width?: number;
            height?: number;
            type?: string;
        };
    };
}

export interface DefaultGalleryMediaApiModel {
    id: number;
    type?: string;
    preview?: string;
    fullsize?: string;
    url?: string;
    thumbnail_url?: string;
}

export interface CampaignMediaGroupApiModel {
    id: number;
    name: string;
    items: any[];
}

export interface CampaignMediaGroupedResponse {
    mode: 'grouped' | 'default' | 'fallback';
    groups: CampaignMediaGroupApiModel[];
}

@Injectable({ providedIn: 'root' })
export class PhotoGalleryHttpService extends BaseHttpService {
    constructor(httpClient: HttpClient) {
        super(httpClient, '/photo-galleries');
    }

    public list(companyId: number, includeInactive = false): Observable<PhotoGalleryApiModel[]> {
        // Bust browser/proxy cache so activate/deactivate is reflected without a hard refresh.
        const bust = `_=${Date.now()}`;
        const path = includeInactive
            ? `/companies/${companyId}/?include_inactive=1&${bust}`
            : `/companies/${companyId}/?${bust}`;
        return this.get<any>(path).pipe(
            map((response: any) => (response || []) as PhotoGalleryApiModel[])
        );
    }

    /** Company Image Gallery source: true = Custom galleries, false = Default gallery. */
    public isUsingCustomGallery(companyId: number): Observable<boolean> {
        return this.http.get<any>(
            `${environment.endpointUrl}/companies/${companyId}/setting/images/config/?_=${Date.now()}`
        ).pipe(
            map((response: any) => !!(response && response.is_custom_gallery))
        );
    }

    public listDefaultMedia(companyId: number): Observable<DefaultGalleryMediaApiModel[]> {
        return this.get<any>(`/companies/${companyId}/default-media/`).pipe(
            map((response: any) => (response || []) as DefaultGalleryMediaApiModel[])
        );
    }

    public create(companyId: number, name: string): Observable<PhotoGalleryApiModel> {
        return this.post(`/companies/${companyId}/`, { name }).pipe(
            map((response: any) => response as PhotoGalleryApiModel)
        );
    }

    public update(galleryId: number, payload: Partial<{ name: string; is_active: boolean }>): Observable<PhotoGalleryApiModel> {
        return this.patch(`/${galleryId}/`, payload).pipe(
            map((response: any) => response as PhotoGalleryApiModel)
        );
    }

    public deleteGallery(galleryId: number): Observable<any> {
        return this.delete(`/${galleryId}/`);
    }

    /** Replace the company's "Include in default" galleries; returns the saved ids. */
    public setDefaultGalleries(companyId: number, galleryIds: number[]): Observable<number[]> {
        return this.put(`/companies/${companyId}/default-galleries/`, { gallery_ids: galleryIds }).pipe(
            map((response: any) => (response && response.gallery_ids) || [])
        );
    }

    public listMedia(galleryId: number): Observable<PhotoGalleryMediaItemApiModel[]> {
        return this.get<any>(`/${galleryId}/media/`).pipe(
            map((response: any) => (response || []) as PhotoGalleryMediaItemApiModel[])
        );
    }

    public addMedia(galleryId: number, mediafileIds: number[]): Observable<any> {
        return this.post(`/${galleryId}/media/`, { mediafile_ids: mediafileIds }).pipe(
            map((response: any) => response)
        );
    }

    public removeMedia(galleryId: number, mediafileId: number): Observable<any> {
        return this.delete(`/${galleryId}/media/${mediafileId}/`).pipe(
            map((response: any) => response)
        );
    }

    public listCampaignGalleries(campaignId: number): Observable<PhotoGalleryApiModel[]> {
        return this.get<any>(`/campaigns/${campaignId}/?_=${Date.now()}`).pipe(
            map((response: any) => (response || []) as PhotoGalleryApiModel[])
        );
    }

    public setCampaignGalleries(campaignId: number, galleryIds: number[]): Observable<any> {
        return this.post(`/campaigns/${campaignId}/`, { gallery_ids: galleryIds }).pipe(
            map((response: any) => response)
        );
    }

    public loadCampaignMediaGrouped(campaignId: number): Observable<CampaignMediaGroupedResponse> {
        return this.get<any>(`/campaigns/${campaignId}/media/`).pipe(
            map((response: any) => response as CampaignMediaGroupedResponse)
        );
    }

    /** Main Submit Story: all active custom galleries (or Default stock). */
    public loadCompanyMediaGrouped(companyId: number): Observable<CampaignMediaGroupedResponse> {
        return this.get<any>(`/companies/${companyId}/media/?_=${Date.now()}`).pipe(
            map((response: any) => response as CampaignMediaGroupedResponse)
        );
    }
}
